import React, { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, Loader2, Send } from "lucide-react";
import { SCHOOL, IMAGES } from "@/lib/constants";
import { PageBanner } from "@/components/PageBanner";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/utils2";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) { toast.error("Please fill name, phone and message."); return; }
    setLoading(true);
    try {
      await api.createContact(form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send.");
    } finally { setLoading(false); }
  };

  const cards = [
    { icon: MapPin, title: "Address", lines: [SCHOOL.address] },
    { icon: Phone, title: "Phone", lines: [`Landline: ${SCHOOL.landline}`, SCHOOL.phones.join(" / ")] },
    { icon: Mail, title: "Email", lines: [SCHOOL.email] },
    { icon: Clock, title: "Office Hours", lines: [SCHOOL.officeTiming, `School: ${SCHOOL.schoolTiming}`] },
  ];

  return (
    <div>
      <PageBanner title="Contact Us" subtitle="We'd love to hear from you. Visit, call or send us a message." image={IMAGES.hero} />

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {cards.map((c) => (
            <div key={c.title} className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
              <div className="h-12 w-12 rounded-xl bg-brandred/10 text-brandred grid place-items-center mb-4"><c.icon className="h-6 w-6" /></div>
              <h4 className="font-head font-bold text-navy">{c.title}</h4>
              {c.lines.map((l) => <p key={l} className="text-sm text-slate-600 mt-1">{l}</p>)}
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4">
            <h3 className="font-head font-extrabold text-2xl text-navy">Send us a message</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input data-testid="contact-name" placeholder="Your Name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
              <Input data-testid="contact-phone" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <Input data-testid="contact-email" type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            <Input data-testid="contact-subject" placeholder="Subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} />
            <Textarea data-testid="contact-message" rows={4} placeholder="Your message *" value={form.message} onChange={(e) => set("message", e.target.value)} />
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-brandred hover:bg-brandred/90 text-white rounded-full h-12 font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="mr-1.5 h-4 w-4" /> Send Message</>}
            </Button>
          </form>

          <div className="rounded-3xl overflow-hidden border border-slate-200 shadow-sm min-h-[400px]">
            <iframe title="School location" src={SCHOOL.mapEmbed} className="w-full h-full min-h-[400px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
      </section>
    </div>
  );
}
